import React, { FC, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceArea,
  ReferenceDot,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import StableResponsiveContainer from '../../general/stable-responsive-container/stable-responsive-container.component';
import { WorkoutData } from '../../../models/training.models';
import {
  findPeakSample,
  findTroughSample,
  formatDistanceTick,
  formatElevationTooltip,
  formatPaceTooltip,
  formatPaceValue,
  formatSpeedTooltip,
  formatWorkoutSampleTime,
  getMetricDomain,
  hasMetricData,
  prepareWorkoutSamples,
  PreparedWorkoutSample,
  WorkoutMetricKey,
} from '../../../helpers/workout-samples.helpers';
import {
  getElevationMeasurementUnit,
  getMeasurementUnit,
  getSpeedMeasurementUnit,
} from '../../../helpers/general.helpers';
import { palette } from '../../../theme';
import './workout-analytics-stacked-graph.component.css';

export type WorkoutAnalyticsLayerKey = 'heartRate' | 'pace' | 'speed' | 'elevation';

export interface WorkoutAnalyticsStackedGraphProps {
  workoutData: WorkoutData;
  selectedSplitNumber?: number | null;
}

const LAYER_CONFIG: Record<
  WorkoutAnalyticsLayerKey,
  { label: string; shortLabel: string; color: string; dataKey: WorkoutMetricKey; yAxisId: string }
> = {
  heartRate: {
    label: 'Heart Rate',
    shortLabel: 'HR',
    color: palette.error.main,
    dataKey: 'smoothHeartRate',
    yAxisId: 'hr',
  },
  pace: {
    label: 'Pace',
    shortLabel: 'Pace',
    color: palette.warning.main,
    dataKey: 'displayPace',
    yAxisId: 'pace',
  },
  speed: {
    label: 'Speed',
    shortLabel: 'Speed',
    color: palette.primary.main,
    dataKey: 'displaySpeed',
    yAxisId: 'speed',
  },
  elevation: {
    label: 'Elevation',
    shortLabel: 'Elev',
    color: palette.text.secondary,
    dataKey: 'displayElevation',
    yAxisId: 'elevation',
  },
};

const DEFAULT_ACTIVE_LAYERS: WorkoutAnalyticsLayerKey[] = ['heartRate', 'elevation'];
const LAYER_ORDER: WorkoutAnalyticsLayerKey[] = ['heartRate', 'pace', 'speed', 'elevation'];

const WorkoutAnalyticsStackedGraph: FC<WorkoutAnalyticsStackedGraphProps> = ({
  workoutData,
  selectedSplitNumber = null,
}) => {
  const [activeLayers, setActiveLayers] = useState<Set<WorkoutAnalyticsLayerKey>>(() => new Set(DEFAULT_ACTIVE_LAYERS));

  const samples = useMemo(() => prepareWorkoutSamples(workoutData.samples), [workoutData.samples]);
  const distanceUnit = getMeasurementUnit();
  const elevationUnit = getElevationMeasurementUnit();
  const speedUnit = getSpeedMeasurementUnit();

  const availableLayers = useMemo(() => {
    return LAYER_ORDER.filter((key) => hasMetricData(samples, LAYER_CONFIG[key].dataKey));
  }, [samples]);

  const showHeartRate = activeLayers.has('heartRate') && availableLayers.includes('heartRate');
  const showPace = activeLayers.has('pace') && availableLayers.includes('pace');
  const showSpeed = activeLayers.has('speed') && availableLayers.includes('speed');
  const showElevation = activeLayers.has('elevation') && availableLayers.includes('elevation');
  const activeCount = [showHeartRate, showPace, showSpeed, showElevation].filter(Boolean).length;

  const hrDomain = useMemo(() => getMetricDomain(samples, 'smoothHeartRate'), [samples]);
  const paceDomain = useMemo(() => getMetricDomain(samples, 'displayPace'), [samples]);
  const speedDomain = useMemo(() => getMetricDomain(samples, 'displaySpeed'), [samples]);
  const elevationDomain = useMemo(() => getMetricDomain(samples, 'displayElevation'), [samples]);

  const peakHr = useMemo(
    () => (showHeartRate ? findPeakSample(samples, 'smoothHeartRate') : null),
    [samples, showHeartRate]
  );
  const peakPace = useMemo(() => (showPace ? findTroughSample(samples, 'displayPace') : null), [samples, showPace]);
  const peakSpeed = useMemo(() => (showSpeed ? findPeakSample(samples, 'displaySpeed') : null), [samples, showSpeed]);
  const peakElevation = useMemo(
    () => (showElevation ? findPeakSample(samples, 'displayElevation') : null),
    [samples, showElevation]
  );

  const splitHighlight = useMemo(() => {
    if (selectedSplitNumber == null || selectedSplitNumber < 1) {
      return null;
    }
    // Splits are 1 KM buckets in display units when metric; for imperial still use 1-unit bands by split index
    const start = selectedSplitNumber - 1;
    const end = selectedSplitNumber;
    const maxDistance = samples.length ? samples[samples.length - 1].displayDistance : 0;
    if (start >= maxDistance) {
      return null;
    }
    return { start, end: Math.min(end, maxDistance) };
  }, [selectedSplitNumber, samples]);

  const hasVisibleLeftAxis = showHeartRate;
  const hasVisibleRightAxis = showElevation && hasVisibleLeftAxis;
  // Keep axis gutters tight — recharts already reserves each YAxis `width` from the plot.
  const axisWidth = 28;
  const leftMargin = 4;
  const rightMargin = hasVisibleRightAxis ? 4 : 8;

  const toggleLayer = (layerKey: WorkoutAnalyticsLayerKey) => {
    if (!availableLayers.includes(layerKey)) {
      return;
    }
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerKey)) {
        if (next.size <= 1) {
          return prev;
        }
        next.delete(layerKey);
      } else {
        next.add(layerKey);
      }
      return next;
    });
  };

  const formatTooltipValue = (layerKey: WorkoutAnalyticsLayerKey, sample: PreparedWorkoutSample): string => {
    switch (layerKey) {
      case 'heartRate':
        return sample.smoothHeartRate != null ? `${Math.round(sample.smoothHeartRate)} bpm` : '—';
      case 'pace':
        return formatPaceTooltip(sample.displayPace);
      case 'speed':
        return formatSpeedTooltip(sample.displaySpeed);
      case 'elevation':
        return formatElevationTooltip(sample.displayElevation);
      default:
        return '—';
    }
  };

  if (!samples.length || availableLayers.length === 0) {
    return <Typography sx={{ p: 2, color: palette.text.secondary }}>No analytics data available.</Typography>;
  }

  return (
    <Box className="workout-analytics-chart">
      <div className="workout-analytics-chart__header">
        <div className="workout-analytics-chart__toggles">
          {LAYER_ORDER.map((key) => {
            const config = LAYER_CONFIG[key];
            const isAvailable = availableLayers.includes(key);
            const isActive = activeLayers.has(key) && isAvailable;
            return (
              <button
                key={key}
                type="button"
                className={`workout-analytics-chart__toggle${isActive ? ' is-active' : ''}`}
                style={{
                  color: isActive ? config.color : palette.text.primary,
                  backgroundColor: isActive ? `${config.color}18` : undefined,
                  opacity: isAvailable ? 1 : 0.4,
                }}
                disabled={!isAvailable}
                onClick={() => toggleLayer(key)}
              >
                <span className="workout-analytics-chart__toggle-dot" style={{ background: config.color }} />
                {config.label}
              </button>
            );
          })}
        </div>
        <div className="workout-analytics-chart__caption">
          {activeCount} Metric{activeCount === 1 ? '' : 's'}
        </div>
      </div>

      <div className="workout-analytics-chart__container">
        {activeCount === 0 ? (
          <div className="workout-analytics-chart__empty">Select a layer to view the chart</div>
        ) : (
          <StableResponsiveContainer width="100%" height={280}>
            <ComposedChart data={samples} margin={{ top: 24, right: rightMargin, left: leftMargin, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={palette.grey[200]} />
              <XAxis
                dataKey="displayDistance"
                type="number"
                domain={['dataMin', 'dataMax']}
                tick={{ fontSize: 11, fill: palette.text.secondary }}
                tickLine={false}
                axisLine={{ stroke: palette.grey[200] }}
                tickFormatter={(value) => formatDistanceTick(Number(value))}
                unit={` ${distanceUnit}`}
              />
              {showHeartRate && (
                <YAxis
                  yAxisId="hr"
                  orientation="left"
                  domain={hrDomain}
                  width={axisWidth}
                  tick={{ fontSize: 9, fill: palette.error.main }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${Math.round(value)}`}
                />
              )}
              {showPace && <YAxis yAxisId="pace" hide domain={paceDomain} reversed />}
              {showSpeed && <YAxis yAxisId="speed" hide domain={speedDomain} />}
              {showElevation && (
                <YAxis
                  yAxisId="elevation"
                  orientation={hasVisibleLeftAxis ? 'right' : 'left'}
                  domain={elevationDomain}
                  width={axisWidth}
                  tick={{ fontSize: 9, fill: palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${Math.round(value)}`}
                />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: palette.background.paper,
                  border: `1px solid ${palette.grey[200]}`,
                  borderRadius: '8px',
                  fontSize: 12,
                }}
                formatter={(_value, name, item) => {
                  const sample = item.payload as PreparedWorkoutSample;
                  const layerKey = (Object.keys(LAYER_CONFIG) as WorkoutAnalyticsLayerKey[]).find(
                    (key) => LAYER_CONFIG[key].label === name
                  );
                  if (!layerKey) {
                    return ['—', name];
                  }
                  return [formatTooltipValue(layerKey, sample), name];
                }}
                labelFormatter={(_label, payload) => {
                  const sample = payload?.[0]?.payload as PreparedWorkoutSample | undefined;
                  if (!sample) {
                    return '';
                  }
                  return `${sample.displayDistance.toFixed(2)} ${distanceUnit} · ${formatWorkoutSampleTime(
                    sample.time
                  )}`;
                }}
              />
              {splitHighlight && (
                <ReferenceArea
                  yAxisId={showHeartRate ? 'hr' : showElevation ? 'elevation' : showPace ? 'pace' : 'speed'}
                  x1={splitHighlight.start}
                  x2={splitHighlight.end}
                  fill={palette.primary.light}
                  fillOpacity={0.45}
                  ifOverflow="extendDomain"
                />
              )}
              {showElevation && (
                <Area
                  yAxisId="elevation"
                  type="monotone"
                  dataKey="displayElevation"
                  name={LAYER_CONFIG.elevation.label}
                  stroke={LAYER_CONFIG.elevation.color}
                  fill={LAYER_CONFIG.elevation.color}
                  fillOpacity={0.12}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {showHeartRate && (
                <Area
                  yAxisId="hr"
                  type="monotone"
                  dataKey="smoothHeartRate"
                  name={LAYER_CONFIG.heartRate.label}
                  stroke={LAYER_CONFIG.heartRate.color}
                  fill={LAYER_CONFIG.heartRate.color}
                  fillOpacity={0.18}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {showPace && (
                <Line
                  yAxisId="pace"
                  type="monotone"
                  dataKey="displayPace"
                  name={LAYER_CONFIG.pace.label}
                  stroke={LAYER_CONFIG.pace.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {showSpeed && (
                <Line
                  yAxisId="speed"
                  type="monotone"
                  dataKey="displaySpeed"
                  name={LAYER_CONFIG.speed.label}
                  stroke={LAYER_CONFIG.speed.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
              {peakHr?.smoothHeartRate != null && (
                <ReferenceDot
                  yAxisId="hr"
                  x={peakHr.displayDistance}
                  y={peakHr.smoothHeartRate}
                  r={4}
                  fill={LAYER_CONFIG.heartRate.color}
                  stroke={palette.background.paper}
                  strokeWidth={2}
                >
                  <Label
                    value={String(Math.round(peakHr.smoothHeartRate))}
                    position="top"
                    fill={LAYER_CONFIG.heartRate.color}
                    fontSize={12}
                    fontWeight={700}
                  />
                </ReferenceDot>
              )}
              {peakPace?.displayPace != null && (
                <ReferenceDot
                  yAxisId="pace"
                  x={peakPace.displayDistance}
                  y={peakPace.displayPace}
                  r={4}
                  fill={LAYER_CONFIG.pace.color}
                  stroke={palette.background.paper}
                  strokeWidth={2}
                >
                  <Label
                    value={formatPaceValue(peakPace.displayPace)}
                    position="top"
                    fill={LAYER_CONFIG.pace.color}
                    fontSize={12}
                    fontWeight={700}
                  />
                </ReferenceDot>
              )}
              {peakSpeed?.displaySpeed != null && (
                <ReferenceDot
                  yAxisId="speed"
                  x={peakSpeed.displayDistance}
                  y={peakSpeed.displaySpeed}
                  r={4}
                  fill={LAYER_CONFIG.speed.color}
                  stroke={palette.background.paper}
                  strokeWidth={2}
                >
                  <Label
                    value={`${peakSpeed.displaySpeed.toFixed(1)} ${speedUnit}`}
                    position="top"
                    fill={LAYER_CONFIG.speed.color}
                    fontSize={12}
                    fontWeight={700}
                  />
                </ReferenceDot>
              )}
              {peakElevation?.displayElevation != null && (
                <ReferenceDot
                  yAxisId="elevation"
                  x={peakElevation.displayDistance}
                  y={peakElevation.displayElevation}
                  r={4}
                  fill={LAYER_CONFIG.elevation.color}
                  stroke={palette.background.paper}
                  strokeWidth={2}
                >
                  <Label
                    value={`${Math.round(peakElevation.displayElevation)}${elevationUnit}`}
                    position="top"
                    fill={LAYER_CONFIG.elevation.color}
                    fontSize={12}
                    fontWeight={700}
                  />
                </ReferenceDot>
              )}
            </ComposedChart>
          </StableResponsiveContainer>
        )}
      </div>

      <div className="workout-analytics-chart__legend">
        {LAYER_ORDER.map((key) => {
          const config = LAYER_CONFIG[key];
          return (
            <span key={key} className="workout-analytics-chart__legend-item">
              <span className="workout-analytics-chart__legend-dot" style={{ background: config.color }} />
              {config.shortLabel}
            </span>
          );
        })}
      </div>
    </Box>
  );
};

export default WorkoutAnalyticsStackedGraph;
